/** tm/legal/useBackLayer.ts — 휴대폰·브라우저 「뒤로 가기」가 열린 겹 화면(상세·필터 시트·설정·새 소식 패널)을 먼저 닫게 한다.
 *  · 겹 화면이 열리면 기록을 하나 쌓는다(history.pushState). 뒤로 가기 → 가장 위의 겹 화면만 닫힘.
 *  · 실제 앱은 App.jsx 가 popstate 를 받아 'tm-back' 이벤트를 쏜다 → 캡처 단계에서 먼저 받아 겹 화면을 닫고 전파를 멈춘다
 *    (그래서 업무관리 셸이 대시보드로 이동하지 않는다). 겹 화면이 없으면 원래대로 셸이 처리한다.
 *  · 화면 안의 버튼으로 닫으면 쌓아 둔 기록은 남겨 두고(spare) 다음에 열 때 다시 쓴다 — history.back() 을 부르면 셸이 이동해 버리기 때문. */
import { useEffect, useRef } from 'react';

interface Layer { id: number; close: () => void }
const stack: Layer[] = [];
let spare = 0;
let handledAt = 0;
let seq = 0;
let wired = false;

function closeTop(): boolean {
  const top = stack.pop();
  if (!top) return false;
  handledAt = Date.now();
  top.close();
  return true;
}

function wire() {
  if (wired || typeof window === 'undefined') return;
  wired = true;
  window.addEventListener('tm-back', e => { if (stack.length) { e.stopImmediatePropagation(); closeTop(); } }, true);
  window.addEventListener('popstate', () => {
    if (Date.now() - handledAt < 80) return; // 같은 뒤로 가기를 tm-back 으로 이미 처리
    if (!closeTop() && spare > 0) spare -= 1;
  });
}

/** open 인 동안 뒤로 가기로 onClose 가 불린다 */
export function useBackLayer(open: boolean, onClose: () => void): void {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    wire();
    const layer: Layer = { id: ++seq, close: () => closeRef.current() };
    stack.push(layer);
    if (spare > 0) spare -= 1;
    else { try { window.history.pushState(window.history.state, ''); } catch { /* 기록을 못 쌓는 환경 */ } }
    return () => {
      const i = stack.findIndex(l => l.id === layer.id);
      if (i >= 0) { stack.splice(i, 1); spare += 1; } // 화면 안에서 닫힘 → 기록은 다음에 다시 씀
    };
  }, [open]);
}
